import { ArrowRight, CheckCircle2, CircleDot, Info, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const WELCOME_PARAM = "welcome";
// Set the first time the dialog is dismissed, so the first-visit auto-open only
// ever fires once per browser. The `?welcome=1` login path ignores this flag and
// always opens, so a fresh sign-in still pops the overview.
const SEEN_KEY = "vi.welcome.seen";

// Module-level latch so the auto-open survives React Strict Mode's dev-only
// mount → unmount → remount dance: the first mount strips `?welcome=1` from the
// URL and sets `pendingWelcome = true`; the remount sees no URL param but reads
// the latch and re-opens the dialog. The latch is cleared when the user
// dismisses the dialog so a manual refresh after that does not re-open it.
let pendingWelcome = false;

export function WelcomeDialog() {
  const [open, setOpen] = useState(false);

  // Auto-open in two cases: the URL carries `?welcome=1` (the login page appends
  // this so the overview pops on every fresh sign-in), or this browser has never
  // seen the dialog before (first visit — e.g. opening the app with an existing
  // session, where no login redirect ran).
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get(WELCOME_PARAM) === "1") {
      url.searchParams.delete(WELCOME_PARAM);
      const cleaned = url.pathname + (url.search ? url.search : "") + url.hash;
      window.history.replaceState(null, "", cleaned);
      pendingWelcome = true;
    } else if (!localStorage.getItem(SEEN_KEY)) {
      pendingWelcome = true;
    }
    // One-shot open driven by the URL/latch — not a render loop, so the generic
    // "no setState in effect" rule does not apply here.
    if (pendingWelcome) {
      setOpen(true);
    }
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      pendingWelcome = false;
      // Remember the dismissal so the first-visit auto-open doesn't fire again.
      if (typeof window !== "undefined") {
        localStorage.setItem(SEEN_KEY, "1");
      }
    }
    setOpen(next);
  };

  return <WelcomeDialogControlled open={open} onOpenChange={handleOpenChange} />;
}

// Opens the welcome dialog. Renders to match the SKU-velocity rail footer: a
// full-width labeled row when the rail is expanded, a centered icon when it's
// collapsed (mirroring the profile / voting / sign-out rows next to it).
export function WelcomeDialogTrigger({
  collapsed = false,
  label = "About this demo",
}: {
  collapsed?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={cn(
          "transition-colors",
          collapsed
            ? "text-primary-foreground flex h-10 w-full items-center justify-center rounded-md hover:bg-white/10"
            : "text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm",
        )}
      >
        <Info className="h-4 w-4 shrink-0" />
        {!collapsed && <span>{label}</span>}
      </button>
      <WelcomeDialogControlled open={open} onOpenChange={setOpen} />
    </>
  );
}

function WelcomeDialogControlled({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-h-[calc(100vh-2rem)] max-w-2xl gap-0 overflow-x-hidden overflow-y-auto p-0 sm:rounded-lg">
        <div
          aria-hidden
          className="bg-primary pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-[0.06] blur-3xl"
        />
        {/*
          Overview copy for the UKDC pilot. Names nothing beyond what the app
          actually does — the conversational agent and pinnable dashboard are
          being adapted and sit on the roadmap, not in "live today". Every
          surface carries the fictive-data disclaimer.
        */}
        <div className="relative grid grid-cols-1 gap-6 p-6 sm:p-8">
          <DialogHeader>
            <span className="text-primary text-xs font-medium tracking-[0.18em] uppercase">
              J&amp;J Vision · UKDC pilot
            </span>
            <DialogTitle className="font-heading text-foreground mt-2 text-2xl leading-tight font-normal tracking-tight sm:text-3xl">
              One screen to decide what&rsquo;s worth holding where.
            </DialogTitle>
            <DialogDescription className="text-muted text-sm leading-relaxed">
              Vision Intelligence is a site-level SKU decision surface for the UKDC pilot. Four
              screens, built over one shared snapshot, take you from the whole portfolio to an
              actionable list — plotting every SKU by how fast it moves against the money or space
              it consumes, testing the held total against the site&rsquo;s pick-face ceiling through
              2029, and turning that into slotting and relocation lists you can accept, remove, and
              export. Everything shown here runs on fictive data, to demonstrate what&rsquo;s
              possible with current technology.
            </DialogDescription>
          </DialogHeader>

          <div className="border-border bg-background flex flex-col gap-3 rounded-md border p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-4 w-4" />
              <p className="text-foreground text-sm font-semibold">What&rsquo;s live today</p>
            </div>
            <ul className="text-foreground space-y-2 text-sm leading-6">
              <Bullet>
                SKU velocity — every SKU held at the UKDC on one field, by velocity against the
                money or space it consumes
              </Bullet>
              <Bullet>
                Capacity scenarios — held SKUs against the site&rsquo;s pick-face ceiling through
                2029, with levers to free or buy space and NPI intake to phase
              </Bullet>
              <Bullet>
                Location review &amp; Non-movers — actionable slotting and relocation lists you can
                accept, remove, and export
              </Bullet>
              <Bullet>
                On Capacity scenarios, ask the agent in plain language — it has the tools to set the
                scenario levers for you (free or buy space, phase NPI intake) and re-read the result
              </Bullet>
              <Bullet>Read-only — every screen reads the shared snapshot; none write back</Bullet>
            </ul>
            <div className="mt-2 flex items-center gap-2">
              <CircleDot className="text-warm-gray-06 h-4 w-4" />
              <p className="text-muted text-sm font-semibold">On the horizon</p>
            </div>
            <ul className="text-muted-foreground space-y-2 text-sm leading-6">
              <Bullet tone="muted">
                Extending beyond the UKDC pilot to other J&amp;J Vision distribution sites
              </Bullet>
              <Bullet tone="muted">Moving from fictive data to governed, real data</Bullet>
              <Bullet tone="muted">
                Writing accepted slotting and relocation decisions back into planning systems
              </Bullet>
            </ul>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Drops the user straight onto Scenarios with the agent chat open and a first
                question already asked — carried in router state so it fires once and leaves no
                trace in the URL (a refresh won't re-trigger it). */}
            <Button asChild size="lg" onClick={() => onOpenChange(false)}>
              <Link to="/scenarios" state={{ ask: "Close the gap for me" }}>
                See it in action
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Quiet route into the full docs — closes the dialog on navigate
              (the shell keeps it mounted, so it won't self-dismiss otherwise). */}
          <Link
            to="/guide"
            onClick={() => onOpenChange(false)}
            className="text-muted hover:text-primary -mt-2 inline-flex items-center gap-1 text-sm font-medium transition-colors"
          >
            Read the full guide
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>

          {/* Persistent fictive-data disclaimer. */}
          <p className="text-muted-foreground text-xs">
            Fictive data for the sake of demonstrating functionality.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bullet({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "muted";
}) {
  // Live items get a green check; roadmap items a muted "not yet" dot.
  const Icon = tone === "muted" ? CircleDot : CheckCircle2;
  return (
    <li className="flex items-start gap-2">
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "muted" ? "text-warm-gray-06" : "text-[#328714]",
        )}
      />
      <span>{children}</span>
    </li>
  );
}
